import { createContext, useContext, useMemo } from "react";
import type { ReactElement, ReactNode } from "react";
import { useLocation } from "react-router-dom";

/**
 * Cycle 22 ADR-008 + addendum (Tenant URL Routing Strategy).
 *
 * Detects current tenant slug from two sources (priority order):
 *   1. Subdomain — `window.location.hostname.split('.')[0]`
 *      when hostname starts with anything НЕ из reserved set
 *      (portal, admin, www, demo, localhost).
 *   2. URL path — `/t/<slug>/*` pattern.
 *   3. Else null (legacy / default tenant resolved by backend).
 *
 * Used by:
 *   - TenantLandingPage (welcome screen for new tenants)
 *   - AdminTenantsPage URL preview generation
 *   - apiClient header injection (X-Tenant-Slug) on cross-tenant requests
 */

interface TenantContextValue {
  /** Slug if detected (acme-pilot), else null (host portal/admin/www/etc). */
  slug: string | null;
  /** Detection source — useful for debugging / analytics. */
  source: "subdomain" | "path" | "none";
  /** Full URL без tenant prefix — useful для navigation помощников. */
  pathWithoutTenantPrefix: string;
  /** True если current request — на subdomain (тенant-isolated UI). */
  isSubdomainTenant: boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

const RESERVED_SUBDOMAINS = new Set([
  "portal",
  "admin",
  "www",
  "demo",
  "localhost",
  "127",
  "0",
  "stage",
  "staging",
  "test",
  "dev",
]);

// DNS-safe slug pattern (см. backend tenant.py is_valid_slug)
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}$/;

function detectFromHostname(hostname: string): string | null {
  const parts = hostname.split(".");
  const first = parts[0]?.toLowerCase();
  if (!first || RESERVED_SUBDOMAINS.has(first)) return null;
  if (!SLUG_PATTERN.test(first)) return null;
  // Skip pure IP fragments / "192" etc.
  if (/^\d+$/.test(first)) return null;
  return first;
}

function detectFromPath(pathname: string): { slug: string | null; rest: string } {
  // Pattern: /t/<slug>/<rest...> or /t/<slug>
  const m = pathname.match(/^\/t\/([a-z0-9][a-z0-9-]{0,62})(\/.*)?$/);
  if (!m || !m[1]) return { slug: null, rest: pathname };
  return { slug: m[1], rest: m[2] ?? "/" };
}

export function TenantProvider({ children }: { children: ReactNode }): ReactElement {
  const location = useLocation();

  const value = useMemo<TenantContextValue>(() => {
    // Try subdomain first
    const fromHost =
      typeof window !== "undefined" ? detectFromHostname(window.location.hostname) : null;

    if (fromHost) {
      return {
        slug: fromHost,
        source: "subdomain",
        pathWithoutTenantPrefix: location.pathname,
        isSubdomainTenant: true,
      };
    }

    // Fallback to path
    const fromPath = detectFromPath(location.pathname);
    if (fromPath.slug) {
      return {
        slug: fromPath.slug,
        source: "path",
        pathWithoutTenantPrefix: fromPath.rest,
        isSubdomainTenant: false,
      };
    }

    return {
      slug: null,
      source: "none",
      pathWithoutTenantPrefix: location.pathname,
      isSubdomainTenant: false,
    };
  }, [location.pathname]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used within TenantProvider");
  return ctx;
}

/**
 * Pure helper for tests / non-React consumers.
 * Exposed for unit-testing detection logic.
 */
export const __TEST_ONLY = {
  detectFromHostname,
  detectFromPath,
  RESERVED_SUBDOMAINS,
};
